import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import axios from 'axios';
import AdminHeader from '../../Common/Layout/AdminHeader';
import { rexTechTheme } from '../../Common/Theme';
import { 
  UserPlus, 
  User, 
  Users,
  Mail, 
  Phone, 
  Lock, 
  Building, 
  Shield,
  Save, 
  Edit2,
  Trash2,
  X,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Eye,
  EyeOff,
  Briefcase,
  Search,
  Filter,
  Check,
  XCircle,
  Globe,
  CreditCard,
  Truck,
  Key
} from 'lucide-react';

// Import your API configuration
import config from '../../Common/Configurations/APIConfig';

const UserManagement = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('all');
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 500,
    totalPages: 1,
    totalRecords: 0
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editUserId, setEditUserId] = useState('');

  const [formData, setFormData] = useState({
    userName: '',
    email: '',
    phoneNumber: '',
    name: '',
    password: '',
    roleName: 'COMPANYUSER',
    branchId: '',
    isActive: true
  });

  const [errors, setErrors] = useState({});

  // Fetch data when component mounts
  useEffect(() => {
    fetchUsers();
    fetchBranches();
  }, []);

  // Filter users when search term or filters change
  useEffect(() => {
    let filtered = users;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        (user.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.phoneNumber?.includes(searchTerm)) ||
        (user.branchName?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply branch filter
    if (selectedBranchFilter !== 'all') {
      filtered = filtered.filter(user => user.branchId === selectedBranchFilter);
    }
    
    setFilteredUsers(filtered);
  }, [searchTerm, users, selectedBranchFilter]);

  // ================= API Calls =================

  const fetchUsers = async () => {
    try {
      setIsFetching(true);
      const response = await axios.get(
        `${config.APIACTIVEURL}${config.GETALLUSERS}?pageNumber=${pagination.pageNumber}&pageSize=${pagination.pageSize}`,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data && response.data.succeeded) {
        const userData = response.data.data || [];
        setUsers(userData);
        setFilteredUsers(userData);
        setPagination({
          pageNumber: response.data.pageNumber || 1,
          pageSize: response.data.pageSize || 500,
          totalPages: response.data.totalPages || 1,
          totalRecords: response.data.totalRecords || userData.length
        });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setErrorMessage('Failed to load users');
    } finally {
      setIsFetching(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(
        `${config.APIACTIVEURL}${config.GETALLBRANCHES}`,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data) {
        setBranches(response.data);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const createUser = async (userData) => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      const createPayload = {
        userName: userData.email, // Use email as username
        email: userData.email,
        phoneNumber: userData.phoneNumber,
        name: userData.name,
        password: userData.password,
        roleName: 'COMPANYUSER', // Fixed role
        branchId: userData.branchId,
        isActive: userData.isActive
      };

      const response = await axios.post(
        `${config.APIACTIVEURL}${config.CREATEUSER}`,
        createPayload,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.status === 200 && response.data === true) {
        setSuccessMessage('User created successfully!');
        setShowModal(false);
        resetForm();
        fetchUsers(); // Refresh the list
        
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrorMessage('Failed to create user. Please try again.');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      
      if (error.response) {
        if (error.response.status === 400) {
          setErrorMessage('Username already exists. Please use a different email.');
        } else if (error.response.data?.message) {
          setErrorMessage(`Failed: ${error.response.data.message}`);
        } else {
          setErrorMessage('Failed to create user. Please check your input.');
        }
      } else if (error.request) {
        setErrorMessage('Network error. Please check your connection.');
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      const updatePayload = {
        userId: userId,
        userName: userData.email, // Use email as username
        email: userData.email,
        phoneNumber: userData.phoneNumber,
        name: userData.name,
        roleName: 'COMPANYUSER', // Fixed role
        branchId: userData.branchId,
        isActive: userData.isActive
      };

      // Only include password if it's provided
      if (userData.password && userData.password.trim() !== '') {
        updatePayload.password = userData.password;
      }

      const response = await axios.put(
        `${config.APIACTIVEURL}${config.UPDATEUSER}`,
        updatePayload,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.status === 200 && response.data === true) {
        setSuccessMessage('User updated successfully!');
        setShowModal(false);
        resetForm();
        fetchUsers(); // Refresh the list
        
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrorMessage('Failed to update user. Please try again.');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      
      if (error.response) {
        if (error.response.status === 400) {
          setErrorMessage('Update failed. Please check your input.');
        } else if (error.response.data?.message) {
          setErrorMessage(`Failed: ${error.response.data.message}`);
        } else {
          setErrorMessage('Failed to update user.');
        }
      } else if (error.request) {
        setErrorMessage('Network error. Please check your connection.');
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    try {
      setIsLoading(true);
      setErrorMessage('');

      const response = await axios.delete(
        `${config.APIACTIVEURL}${config.DELETEUSER}/${userId}`,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.status === 200 && response.data === true) {
        setSuccessMessage('User deleted successfully!');
        setShowDeleteModal(false);
        fetchUsers();
        
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setErrorMessage('Failed to delete user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ================= Form Handling =================

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    setErrorMessage('');
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Email validation (REQUIRED - also serves as username)
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Phone validation
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }
    
    // Password validation (only required for new users, optional for edit)
    if (!isEditMode && !formData.password) {
      newErrors.password = 'Password is required';
    } else if (!isEditMode && formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    } else if (isEditMode && formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters (leave empty to keep current)';
    }
    
    // Branch validation (REQUIRED)
    if (!formData.branchId) {
      newErrors.branchId = 'Please select a branch';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (isEditMode) {
      await updateUser(editUserId, formData);
    } else {
      await createUser(formData);
    }
  };

  const resetForm = () => {
    setFormData({
      userName: '',
      email: '',
      phoneNumber: '',
      name: '',
      password: '',
      roleName: 'COMPANYUSER',
      branchId: '',
      isActive: true
    });
    setErrors({});
    setErrorMessage('');
    setIsEditMode(false);
    setEditUserId('');
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (user) => {
    resetForm();
    setIsEditMode(true);
    setEditUserId(user.userId);
    
    setFormData({
      userName: user.userName || '',
      email: user.email || '',
      phoneNumber: user.phoneNumber || '',
      name: user.name || '',
      password: '', // Leave password empty for edit
      roleName: 'COMPANYUSER',
      branchId: user.branchId || '',
      isActive: user.isActive !== undefined ? user.isActive : true
    });
    
    setShowModal(true);
  };

  const openDeleteModal = (userId) => {
    setUserToDelete(userId);
    setShowDeleteModal(true);
  };

  const getRoleIcon = () => {
    return <User size={16} />;
  };

  const getRoleBadgeColor = () => {
    return rexTechTheme.colors.primary.main;
  };

  const getRoleDisplayName = () => {
    return 'Company User';
  };

  // ================= UI Components =================

  const LoadingSpinner = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: rexTechTheme.spacing.xxl,
      color: rexTechTheme.colors.primary.main
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: `3px solid ${rexTechTheme.colors.primary.lightest}`,
        borderTop: `3px solid ${rexTechTheme.colors.primary.main}`,
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: rexTechTheme.spacing.md
      }} />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <span style={{ color: rexTechTheme.colors.neutral.darkGray }}>
        Loading users...
      </span>
    </div>
  );

  const EmptyState = () => (
    <div style={{
      textAlign: 'center',
      padding: rexTechTheme.spacing.xxl,
      color: rexTechTheme.colors.neutral.darkGray
    }}>
      <Users size={48} style={{ 
        marginBottom: rexTechTheme.spacing.md,
        color: rexTechTheme.colors.neutral.gray,
        opacity: 0.5
      }} />
      <h3 style={{ 
        color: rexTechTheme.colors.neutral.darkGray,
        marginBottom: rexTechTheme.spacing.sm
      }}>
        No Users Found
      </h3>
      <p style={{ marginBottom: rexTechTheme.spacing.lg }}>
        Get started by creating your first user
      </p>
      <button
        onClick={openCreateModal}
        style={{
          padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
          background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
          color: rexTechTheme.colors.neutral.white,
          border: 'none',
          borderRadius: rexTechTheme.borderRadius.medium,
          fontSize: rexTechTheme.typography.fontSize.regular,
          fontWeight: rexTechTheme.typography.fontWeight.semiBold,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          gap: rexTechTheme.spacing.sm,
          margin: '0 auto',
          boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
          ':hover': {
            background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
          }
        }}
      >
        <UserPlus size={18} />
        Create First User
      </button>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: rexTechTheme.colors.neutral.lightGray,
      fontFamily: rexTechTheme.typography.fontFamily
    }}>
      <AdminHeader />
      
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: rexTechTheme.spacing.xl
      }}>
        {/* Header Section */}
        <div style={{
          marginBottom: rexTechTheme.spacing.xl
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: rexTechTheme.spacing.lg
          }}>
            <div>
              <h1 style={{
                fontSize: rexTechTheme.typography.fontSize.xxlarge,
                color: rexTechTheme.colors.primary.dark,
                fontWeight: rexTechTheme.typography.fontWeight.bold,
                marginBottom: rexTechTheme.spacing.xs,
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm
              }}>
                <Users size={32} />
                User Management
              </h1>
              <p style={{
                color: rexTechTheme.colors.neutral.darkGray,
                fontSize: rexTechTheme.typography.fontSize.regular,
                margin: 0
              }}>
                Manage company users across branches
              </p>
            </div>
            
            <button
              onClick={openCreateModal}
              style={{
                padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                color: rexTechTheme.colors.neutral.white,
                border: 'none',
                borderRadius: rexTechTheme.borderRadius.medium,
                fontSize: rexTechTheme.typography.fontSize.regular,
                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm,
                boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                ':hover': {
                  background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                }
              }}
            >
              <UserPlus size={18} />
              Add New User
            </button>
          </div>

          {/* Search and Filters */}
          <div style={{
            backgroundColor: rexTechTheme.colors.neutral.white,
            borderRadius: rexTechTheme.borderRadius.large,
            padding: rexTechTheme.spacing.lg,
            boxShadow: rexTechTheme.shadows.light,
            border: `1px solid ${rexTechTheme.colors.neutral.gray}`
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gap: rexTechTheme.spacing.md,
              marginBottom: rexTechTheme.spacing.md,
              '@media (max-width: 768px)': {
                gridTemplateColumns: '1fr'
              }
            }}>
              {/* Search Input */}
              <div style={{ position: 'relative' }}>
                <Search 
                  size={20} 
                  style={{
                    position: 'absolute',
                    left: rexTechTheme.spacing.md,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: rexTechTheme.colors.neutral.darkGray
                  }}
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search users by name, email, phone, or branch..."
                  style={{
                    width: '100%',
                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                    paddingLeft: `calc(${rexTechTheme.spacing.xl} + 20px)`,
                    border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    borderRadius: rexTechTheme.borderRadius.medium,
                    fontSize: rexTechTheme.typography.fontSize.regular,
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    ':focus': {
                      borderColor: rexTechTheme.colors.primary.main,
                      boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                    }
                  }}
                />
              </div>
              
              {/* Branch Filter */}
              <div>
                <select
                  value={selectedBranchFilter}
                  onChange={(e) => setSelectedBranchFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: rexTechTheme.spacing.md,
                    border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    borderRadius: rexTechTheme.borderRadius.medium,
                    fontSize: rexTechTheme.typography.fontSize.regular,
                    backgroundColor: rexTechTheme.colors.neutral.white,
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                >
                  <option value="all">All Branches</option>
                  {branches.map(branch => (
                    <option key={branch.branchId} value={branch.branchId}>
                      {branch.branchName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Stats and Messages */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: rexTechTheme.spacing.sm
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm,
                color: rexTechTheme.colors.neutral.darkGray,
                fontSize: rexTechTheme.typography.fontSize.small
              }}>
                <Filter size={16} />
                <span>{filteredUsers.length} of {pagination.totalRecords} users found</span>
              </div>
              
              {/* Success/Error Messages */}
              {successMessage && (
                <div style={{
                  padding: rexTechTheme.spacing.sm,
                  backgroundColor: rexTechTheme.colors.primary.lightest,
                  border: `1px solid ${rexTechTheme.colors.primary.light}`,
                  borderRadius: rexTechTheme.borderRadius.medium,
                  color: rexTechTheme.colors.primary.dark,
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.sm,
                  fontSize: rexTechTheme.typography.fontSize.small
                }}>
                  <CheckCircle size={16} />
                  {successMessage}
                </div>
              )}
              
              {errorMessage && (
                <div style={{
                  padding: rexTechTheme.spacing.sm,
                  backgroundColor: rexTechTheme.colors.accent.lightest,
                  border: `1px solid ${rexTechTheme.colors.accent.orange}`,
                  borderRadius: rexTechTheme.borderRadius.medium,
                  color: rexTechTheme.colors.accent.orange,
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.sm,
                  fontSize: rexTechTheme.typography.fontSize.small
                }}>
                  <AlertCircle size={16} />
                  {errorMessage}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content - Users Table */}
        <div style={{
          backgroundColor: rexTechTheme.colors.neutral.white,
          borderRadius: rexTechTheme.borderRadius.large,
          boxShadow: rexTechTheme.shadows.light,
          border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
          overflow: 'hidden'
        }}>
          {isFetching ? (
            <LoadingSpinner />
          ) : filteredUsers.length === 0 ? (
            <EmptyState />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: '1000px'
              }}>
                <thead>
                  <tr style={{
                    backgroundColor: rexTechTheme.colors.neutral.lightGray,
                    borderBottom: `2px solid ${rexTechTheme.colors.neutral.gray}`
                  }}>
                    <th style={{
                      padding: rexTechTheme.spacing.lg,
                      textAlign: 'left',
                      fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                      color: rexTechTheme.colors.primary.dark,
                      fontSize: rexTechTheme.typography.fontSize.regular,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      width: '45%'
                    }}>
                      User Details
                    </th>
                    <th style={{
                      padding: rexTechTheme.spacing.lg,
                      textAlign: 'left',
                      fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                      color: rexTechTheme.colors.primary.dark,
                      fontSize: rexTechTheme.typography.fontSize.regular,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      width: '35%'
                    }}>
                      Branch
                    </th>
                    <th style={{
                      padding: rexTechTheme.spacing.lg,
                      textAlign: 'left',
                      fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                      color: rexTechTheme.colors.primary.dark,
                      fontSize: rexTechTheme.typography.fontSize.regular,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      width: '15%'
                    }}>
                      Status
                    </th>
                    <th style={{
                      padding: rexTechTheme.spacing.lg,
                      textAlign: 'center',
                      fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                      color: rexTechTheme.colors.primary.dark,
                      fontSize: rexTechTheme.typography.fontSize.regular,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      width: '20%'
                    }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => (
                    <tr 
                      key={user.userId}
                      style={{
                        backgroundColor: index % 2 === 0 
                          ? rexTechTheme.colors.neutral.white 
                          : rexTechTheme.colors.neutral.lightGray,
                        borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                        transition: 'all 0.2s ease',
                        ':hover': {
                          backgroundColor: rexTechTheme.colors.primary.lightest
                        }
                      }}
                    >
                      <td style={{
                        padding: rexTechTheme.spacing.lg
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: rexTechTheme.spacing.md
                        }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: rexTechTheme.colors.primary.lightest,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            {getRoleIcon()}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                              color: rexTechTheme.colors.primary.dark,
                              fontSize: rexTechTheme.typography.fontSize.regular,
                              marginBottom: rexTechTheme.spacing.xs
                            }}>
                              {user.name || 'N/A'}
                            </div>
                            
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: rexTechTheme.spacing.sm,
                              marginBottom: rexTechTheme.spacing.xs
                            }}>
                              <Mail size={14} color={rexTechTheme.colors.neutral.darkGray} />
                              <span style={{
                                fontSize: rexTechTheme.typography.fontSize.small,
                                color: rexTechTheme.colors.neutral.darkGray,
                                wordBreak: 'break-word'
                              }}>
                                {user.email}
                              </span>
                            </div>
                            
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: rexTechTheme.spacing.sm,
                              marginBottom: rexTechTheme.spacing.xs
                            }}>
                              <Phone size={14} color={rexTechTheme.colors.neutral.darkGray} />
                              <span style={{
                                fontSize: rexTechTheme.typography.fontSize.small,
                                color: rexTechTheme.colors.neutral.darkGray
                              }}>
                                {user.phoneNumber || 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{
                        padding: rexTechTheme.spacing.lg
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: rexTechTheme.spacing.sm
                        }}>
                          <Building size={14} color={rexTechTheme.colors.primary.dark} />
                          <span style={{
                            fontSize: rexTechTheme.typography.fontSize.small,
                            color: rexTechTheme.colors.primary.dark,
                            fontWeight: rexTechTheme.typography.fontWeight.medium
                          }}>
                            {user.branchName || 'Not assigned'}
                          </span>
                        </div>
                      </td>
                      <td style={{
                        padding: rexTechTheme.spacing.lg
                      }}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: rexTechTheme.spacing.md
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: rexTechTheme.spacing.sm
                          }}>
                            <div style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: user.isActive ? rexTechTheme.colors.primary.main : rexTechTheme.colors.neutral.gray
                            }} />
                            <span style={{
                              color: user.isActive ? rexTechTheme.colors.primary.dark : rexTechTheme.colors.neutral.darkGray,
                              fontWeight: rexTechTheme.typography.fontWeight.medium
                            }}>
                              {user.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td style={{
                        padding: rexTechTheme.spacing.lg,
                        textAlign: 'center'
                      }}>
                        <div style={{
                          display: 'flex',
                          gap: rexTechTheme.spacing.sm,
                          justifyContent: 'center'
                        }}>
                          <button
                            onClick={() => openEditModal(user)}
                            style={{
                              padding: `${rexTechTheme.spacing.sm} ${rexTechTheme.spacing.md}`,
                              backgroundColor: rexTechTheme.colors.primary.lightest,
                              border: `1px solid ${rexTechTheme.colors.primary.light}`,
                              color: rexTechTheme.colors.primary.main,
                              borderRadius: rexTechTheme.borderRadius.small,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: rexTechTheme.spacing.xs,
                              fontSize: rexTechTheme.typography.fontSize.small,
                              fontWeight: rexTechTheme.typography.fontWeight.medium,
                              ':hover': {
                                backgroundColor: rexTechTheme.colors.primary.light,
                                color: rexTechTheme.colors.neutral.white
                              }
                            }}
                            title="Edit User"
                          >
                            <Edit2 size={14} />
                            Edit
                          </button>
                          <button
                            onClick={() => openDeleteModal(user.userId)}
                            style={{
                              padding: `${rexTechTheme.spacing.sm} ${rexTechTheme.spacing.md}`,
                              backgroundColor: rexTechTheme.colors.primary.lightest,
                              border: `1px solid ${rexTechTheme.colors.accent.orange}`,
                              color: rexTechTheme.colors.accent.orange,
                              borderRadius: rexTechTheme.borderRadius.small,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: rexTechTheme.spacing.xs,
                              fontSize: rexTechTheme.typography.fontSize.small,
                              fontWeight: rexTechTheme.typography.fontWeight.medium,
                              ':hover': {
                                backgroundColor: rexTechTheme.colors.accent.orange,
                                color: rexTechTheme.colors.neutral.white
                              }
                            }}
                            title="Delete User"
                          >
                            <Trash2 size={14} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination Info */}
        {!isFetching && filteredUsers.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: rexTechTheme.spacing.lg,
            backgroundColor: rexTechTheme.colors.neutral.lightGray,
            borderRadius: rexTechTheme.borderRadius.medium,
            marginTop: rexTechTheme.spacing.lg,
            fontSize: rexTechTheme.typography.fontSize.small,
            color: rexTechTheme.colors.neutral.darkGray
          }}>
            <div>
              Showing {filteredUsers.length} of {pagination.totalRecords} users
            </div>
            <div>
              Page {pagination.pageNumber} of {pagination.totalPages}
            </div>
          </div>
        )}

        {/* Create/Edit User Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
          <Modal.Header closeButton style={{
            borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
            backgroundColor: rexTechTheme.colors.neutral.lightGray
          }}>
            <Modal.Title style={{
              color: rexTechTheme.colors.primary.dark,
              fontWeight: rexTechTheme.typography.fontWeight.bold,
              display: 'flex',
              alignItems: 'center',
              gap: rexTechTheme.spacing.sm
            }}>
              {isEditMode ? <Edit2 size={20} /> : <UserPlus size={20} />}
              {isEditMode ? 'Edit User' : 'Create New User'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: rexTechTheme.spacing.xl }}>
            <form onSubmit={handleSubmit}>
              {errorMessage && (
                <div style={{
                  padding: rexTechTheme.spacing.md,
                  backgroundColor: rexTechTheme.colors.accent.lightest,
                  border: `1px solid ${rexTechTheme.colors.accent.orange}`,
                  borderRadius: rexTechTheme.borderRadius.medium,
                  color: rexTechTheme.colors.accent.orange,
                  marginBottom: rexTechTheme.spacing.lg,
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.sm
                }}>
                  <AlertCircle size={18} />
                  {errorMessage}
                </div>
              )}

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: rexTechTheme.spacing.lg,
                marginBottom: rexTechTheme.spacing.lg
              }}>
                {/* Full Name */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: rexTechTheme.spacing.xs,
                    color: rexTechTheme.colors.primary.dark,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    fontSize: rexTechTheme.typography.fontSize.regular
                  }}>
                    Full Name
                    <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '2px' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                    style={{
                      width: '100%',
                      padding: rexTechTheme.spacing.md,
                      border: `2px solid ${errors.name ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                      borderRadius: rexTechTheme.borderRadius.medium,
                      fontSize: rexTechTheme.typography.fontSize.regular,
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      boxSizing: 'border-box',
                      ':focus': {
                        borderColor: rexTechTheme.colors.primary.main,
                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                      }
                    }}
                  />
                  {errors.name && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: rexTechTheme.spacing.xs,
                      marginTop: rexTechTheme.spacing.xs,
                      color: rexTechTheme.colors.accent.orange,
                      fontSize: rexTechTheme.typography.fontSize.small
                    }}>
                      <AlertCircle size={14} />
                      <span>{errors.name}</span>
                    </div>
                  )}
                </div>

                {/* Email - REQUIRED */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: rexTechTheme.spacing.xs,
                    color: rexTechTheme.colors.primary.dark,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    fontSize: rexTechTheme.typography.fontSize.regular
                  }}>
                    <Mail size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Email Address
                    <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '2px' }}>*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="user@example.com"
                    style={{
                      width: '100%',
                      padding: rexTechTheme.spacing.md,
                      border: `2px solid ${errors.email ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                      borderRadius: rexTechTheme.borderRadius.medium,
                      fontSize: rexTechTheme.typography.fontSize.regular,
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      boxSizing: 'border-box',
                      ':focus': {
                        borderColor: rexTechTheme.colors.primary.main,
                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                      }
                    }}
                  />
                  {errors.email && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: rexTechTheme.spacing.xs,
                      marginTop: rexTechTheme.spacing.xs,
                      color: rexTechTheme.colors.accent.orange,
                      fontSize: rexTechTheme.typography.fontSize.small
                    }}>
                      <AlertCircle size={14} />
                      <span>{errors.email}</span>
                    </div>
                  )}
                  <div style={{
                    marginTop: rexTechTheme.spacing.xs,
                    fontSize: rexTechTheme.typography.fontSize.xs,
                    color: rexTechTheme.colors.neutral.darkGray
                  }}>
                    Email will be used as username
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: rexTechTheme.spacing.xs,
                    color: rexTechTheme.colors.primary.dark,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    fontSize: rexTechTheme.typography.fontSize.regular
                  }}>
                    <Phone size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Phone Number
                    <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '2px' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                    style={{
                      width: '100%',
                      padding: rexTechTheme.spacing.md,
                      border: `2px solid ${errors.phoneNumber ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                      borderRadius: rexTechTheme.borderRadius.medium,
                      fontSize: rexTechTheme.typography.fontSize.regular,
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      boxSizing: 'border-box',
                      ':focus': {
                        borderColor: rexTechTheme.colors.primary.main,
                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                      }
                    }}
                  />
                  {errors.phoneNumber && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: rexTechTheme.spacing.xs,
                      marginTop: rexTechTheme.spacing.xs,
                      color: rexTechTheme.colors.accent.orange,
                      fontSize: rexTechTheme.typography.fontSize.small
                    }}>
                      <AlertCircle size={14} />
                      <span>{errors.phoneNumber}</span>
                    </div>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: rexTechTheme.spacing.xs,
                    color: rexTechTheme.colors.primary.dark,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    fontSize: rexTechTheme.typography.fontSize.regular
                  }}>
                    <Lock size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Password
                    {!isEditMode && <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '2px' }}>*</span>}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder={isEditMode ? "Leave empty to keep current password" : "Enter password"}
                      style={{
                        width: '100%',
                        padding: rexTechTheme.spacing.md,
                        paddingRight: '40px',
                        border: `2px solid ${errors.password ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                        borderRadius: rexTechTheme.borderRadius.medium,
                        fontSize: rexTechTheme.typography.fontSize.regular,
                        transition: 'all 0.2s ease',
                        outline: 'none',
                        boxSizing: 'border-box',
                        ':focus': {
                          borderColor: rexTechTheme.colors.primary.main,
                          boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: rexTechTheme.colors.neutral.darkGray
                      }}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {isEditMode ? (
                    <div style={{
                      marginTop: rexTechTheme.spacing.xs,
                      fontSize: rexTechTheme.typography.fontSize.xs,
                      color: rexTechTheme.colors.neutral.darkGray
                    }}>
                      Leave empty to keep current password. Minimum 6 characters if changing.
                    </div>
                  ) : (
                    <div style={{
                      marginTop: rexTechTheme.spacing.xs,
                      fontSize: rexTechTheme.typography.fontSize.xs,
                      color: rexTechTheme.colors.neutral.darkGray
                    }}>
                      Minimum 6 characters
                    </div>
                  )}
                  {errors.password && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: rexTechTheme.spacing.xs,
                      marginTop: rexTechTheme.spacing.xs,
                      color: rexTechTheme.colors.accent.orange,
                      fontSize: rexTechTheme.typography.fontSize.small
                    }}>
                      <AlertCircle size={14} />
                      <span>{errors.password}</span>
                    </div>
                  )}
                </div>

                {/* Branch - REQUIRED */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: rexTechTheme.spacing.xs,
                    color: rexTechTheme.colors.primary.dark,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    fontSize: rexTechTheme.typography.fontSize.regular
                  }}>
                    <Building size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Branch
                    <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '2px' }}>*</span>
                  </label>
                  <select
                    name="branchId"
                    value={formData.branchId}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: rexTechTheme.spacing.md,
                      border: `2px solid ${errors.branchId ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                      borderRadius: rexTechTheme.borderRadius.medium,
                      fontSize: rexTechTheme.typography.fontSize.regular,
                      backgroundColor: rexTechTheme.colors.neutral.white,
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                      ':focus': {
                        borderColor: rexTechTheme.colors.primary.main,
                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                      }
                    }}
                  >
                    <option value="">Select a branch</option>
                    {branches.map(branch => (
                      <option key={branch.branchId} value={branch.branchId}>
                        {branch.branchName}
                      </option>
                    ))}
                  </select>
                  {errors.branchId && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: rexTechTheme.spacing.xs,
                      marginTop: rexTechTheme.spacing.xs,
                      color: rexTechTheme.colors.accent.orange,
                      fontSize: rexTechTheme.typography.fontSize.small
                    }}>
                      <AlertCircle size={14} />
                      <span>{errors.branchId}</span>
                    </div>
                  )}
                </div>

                {/* Hidden Role Field */}
                <input
                  type="hidden"
                  name="roleName"
                  value="COMPANYUSER"
                />
              </div>

              {/* Active Status */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm,
                padding: rexTechTheme.spacing.md,
                backgroundColor: rexTechTheme.colors.primary.lightest,
                borderRadius: rexTechTheme.borderRadius.medium,
                border: `1px solid ${rexTechTheme.colors.primary.lighter}`,
                marginBottom: rexTechTheme.spacing.lg
              }}>
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
                <label htmlFor="isActive" style={{
                  color: rexTechTheme.colors.primary.dark,
                  fontWeight: rexTechTheme.typography.fontWeight.medium,
                  cursor: 'pointer'
                }}>
                  User is active and can login to the system
                </label>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: rexTechTheme.spacing.md
              }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1,
                    padding: rexTechTheme.spacing.md,
                    backgroundColor: rexTechTheme.colors.neutral.lightGray,
                    border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    color: rexTechTheme.colors.neutral.darkGray,
                    borderRadius: rexTechTheme.borderRadius.medium,
                    fontSize: rexTechTheme.typography.fontSize.regular,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    ':hover': {
                      backgroundColor: rexTechTheme.colors.neutral.gray
                    }
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    padding: rexTechTheme.spacing.md,
                    background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                    color: rexTechTheme.colors.neutral.white,
                    border: 'none',
                    borderRadius: rexTechTheme.borderRadius.medium,
                    fontSize: rexTechTheme.typography.fontSize.regular,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: rexTechTheme.spacing.sm,
                    opacity: isLoading ? 0.7 : 1,
                    boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                    ':hover': isLoading ? {} : {
                      background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                    }
                  }}
                >
                  {isLoading ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: `2px solid ${rexTechTheme.colors.neutral.white}`,
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      {isEditMode ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      {isEditMode ? <Save size={18} /> : <UserPlus size={18} />}
                      {isEditMode ? 'Update User' : 'Create User'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </Modal.Body>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton style={{
            borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
            backgroundColor: rexTechTheme.colors.primary.lightest
          }}>
            <Modal.Title style={{
              color: rexTechTheme.colors.accent.orange,
              fontWeight: rexTechTheme.typography.fontWeight.bold,
              display: 'flex',
              alignItems: 'center',
              gap: rexTechTheme.spacing.sm
            }}>
              <AlertCircle size={20} />
              Confirm Deletion
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: rexTechTheme.spacing.xl }}>
            <div style={{ textAlign: 'center', marginBottom: rexTechTheme.spacing.lg }}>
              <XCircle size={48} style={{ 
                color: rexTechTheme.colors.accent.orange,
                marginBottom: rexTechTheme.spacing.md
              }} />
              <h4 style={{ 
                color: rexTechTheme.colors.primary.dark,
                marginBottom: rexTechTheme.spacing.sm
              }}>
                Delete User?
              </h4>
              <p style={{ 
                color: rexTechTheme.colors.neutral.darkGray,
                lineHeight: 1.5
              }}>
                Are you sure you want to delete this user? 
                This action cannot be undone and all associated data will be removed.
              </p>
            </div>
            <div style={{
              display: 'flex',
              gap: rexTechTheme.spacing.md
            }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  flex: 1,
                  padding: rexTechTheme.spacing.md,
                  backgroundColor: rexTechTheme.colors.neutral.lightGray,
                  border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                  color: rexTechTheme.colors.neutral.darkGray,
                  borderRadius: rexTechTheme.borderRadius.medium,
                  cursor: 'pointer',
                  fontSize: rexTechTheme.typography.fontSize.regular,
                  fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                  transition: 'all 0.2s ease',
                  ':hover': {
                    backgroundColor: rexTechTheme.colors.neutral.gray
                  }
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteUser(userToDelete)}
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: rexTechTheme.spacing.md,
                  backgroundColor: rexTechTheme.colors.accent.orange,
                  border: 'none',
                  color: rexTechTheme.colors.neutral.white,
                  borderRadius: rexTechTheme.borderRadius.medium,
                  cursor: 'pointer',
                  fontSize: rexTechTheme.typography.fontSize.regular,
                  fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                  transition: 'all 0.2s ease',
                  opacity: isLoading ? 0.7 : 1,
                  ':hover': isLoading ? {} : {
                    backgroundColor: '#d35400',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(230, 126, 34, 0.3)'
                  }
                }}
              >
                {isLoading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default UserManagement;